import { money } from '../plugins/config';

type RoundingStrategy =
	| 'round'
	| 'floor'
	| 'ceil'
	| 'truncate'
	| 'bankers'
	| 'custom'
	| 'none';

/**
 * round - математическое округление (0.5 вверх)
 * floor - округление вниз (отбрасывание дробной части)
 * ceil - округление вверх
 * truncate - усечение (отбрасывание дробной части без учёта знака)
 * bankers - банковское округление (0.5 до ближайшего чётного)
 * custom - пользовательская логика округления
 *
 * @example
 * round(1.5, 'round') // 2
 * round(1.5, 'floor') // 1
 * round(1.5, 'ceil') // 2
 * round(1.5, 'truncate') // 1
 * round(1.5, 'bankers') // 2
 */

const allowedStrategies: readonly RoundingStrategy[] = [
	'round',
	'floor',
	'ceil',
	'truncate',
	'bankers',
	'custom',
	'none'
] as const;

const normalizeStrategy = (mode: unknown): RoundingStrategy => {
	if (typeof mode !== 'string') {
		return 'floor';
	}
	return (allowedStrategies as readonly string[]).includes(mode)
		? (mode as RoundingStrategy)
		: 'floor';
};

export default function (value: number, type?: RoundingStrategy): number {
	if (type === 'none') {
		return value;
	}

	const configuredMode = money.value.rounding.mode;
	const strategy: RoundingStrategy = normalizeStrategy(
		type ?? configuredMode ?? 'floor'
	);
	const precision = money.value.rounding.precision
		? money.value.rounding.precision
		: 0;
	const factor = precision > 0 ? Math.pow(10, precision) : 1;

	// Fast path when no precision scaling is needed
	if (factor === 1) {
		switch (strategy) {
			case 'round':
				return Math.round(value);
			case 'ceil':
				return Math.ceil(value);
			case 'truncate':
				return Math.trunc(value);
			case 'bankers': {
				const floorScaled = Math.floor(value);
				const fractional = value - floorScaled;
				if (fractional > 0.5) {
					return floorScaled + 1;
				}
				if (fractional < 0.5) {
					return floorScaled;
				}
				const isHalf = Math.abs(fractional - 0.5) <= 1e-10;
				if (isHalf) {
					const shouldRoundUpToEven = (floorScaled + 1) % 2 === 0;
					return shouldRoundUpToEven ? floorScaled + 1 : floorScaled;
				}
				return Math.round(value);
			}
			case 'floor':
			case 'custom':
			default:
				return Math.floor(value);
		}
	}

	const scaled = value * factor;

	switch (strategy) {
		case 'round':
			return Math.round(scaled) / factor;
		case 'ceil':
			return Math.ceil(scaled) / factor;
		case 'truncate':
			return Math.trunc(scaled) / factor;
		case 'bankers': {
			// Banker's rounding (half to even) at arbitrary precision
			const floorScaled = Math.floor(scaled);
			const fractional = scaled - floorScaled;
			if (fractional > 0.5) {
				return (floorScaled + 1) / factor;
			}
			if (fractional < 0.5) {
				return floorScaled / factor;
			}
			// fractional is ~0.5 within floating tolerance
			const isHalf = Math.abs(fractional - 0.5) <= 1e-10;
			if (isHalf) {
				const shouldRoundUpToEven = (floorScaled + 1) % 2 === 0;
				return (shouldRoundUpToEven ? floorScaled + 1 : floorScaled) / factor;
			}
			// Fallback for rare floating anomalies
			return Math.round(scaled) / factor;
		}
		case 'floor':
		case 'custom':
		default:
			return Math.floor(scaled) / factor;
	}
}
